import { useEffect } from "react";
import { createRoute, useParams } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ClubGuard } from "@/components/ClubGuard";
import { useOrganizerClubs } from "@/lib/clubs/queries";
import { useClubChannel } from "@/lib/clubs/useClubChannel";
import { VotingStateMachine } from "@/components/voting/VotingStateMachine";
import {
  apiPhaseToUiPhase,
  applyVotingPhaseChange,
  useCreateVotingSession,
  useDeleteVotingSession,
  useVotingPhaseAdvance,
  useVotingSession,
  votingQueryKey,
} from "@/lib/clubs/voting";
import { clubsRoute } from "./clubs";

function VotingCockpit() {
  const params = useParams({ from: clubsVotingRoute.id });
  const clubId = params.id;
  const qc = useQueryClient();

  const { data: clubs = [] } = useOrganizerClubs();
  const club = clubs.find((c) => c.id === clubId);

  const sessionQuery = useVotingSession(clubId);
  const session = sessionQuery.data ?? null;

  const createSession = useCreateVotingSession(clubId);
  const deleteSession = useDeleteVotingSession(clubId);
  const phaseAdvance = useVotingPhaseAdvance({
    clubId,
    sessionId: session?.id,
    currentPhase: session?.phase,
  });

  useClubChannel(clubId, (event) => {
    if (event.type === "voting:phase_changed") {
      applyVotingPhaseChange(qc, clubId, event.payload);
      return;
    }
    if (
      event.type === "voting:session_created" ||
      event.type === "voting:session_deleted" ||
      event.type === "voting:nomination_added" ||
      event.type === "voting:nomination_removed" ||
      event.type === "voting:ranking_submitted"
    ) {
      qc.invalidateQueries({ queryKey: votingQueryKey(clubId) });
    }
  });

  // Reset confirm-delete dialog state when session disappears
  useEffect(() => {
    if (!session && deleteSession.isPending) deleteSession.reset();
  }, [session, deleteSession]);

  const phase = apiPhaseToUiPhase(session?.phase);

  const handleStart = () => createSession.mutate();
  const handleAdvance = () => phaseAdvance.advance();
  const handleDelete = () => {
    if (window.confirm("Delete this voting session? This cannot be undone.")) {
      deleteSession.mutate();
    }
  };

  if (sessionQuery.isLoading && !session) {
    return (
      <div role="status" aria-live="polite" style={{ padding: 32 }}>
        Loading…
      </div>
    );
  }

  return (
    <VotingStateMachine
      clubName={club?.name ?? "Club"}
      phase={phase}
      session={session}
      isStarting={createSession.isPending}
      isAdvancing={phaseAdvance.isAdvancing}
      isDeleting={deleteSession.isPending}
      error={
        createSession.error ?? phaseAdvance.error ?? deleteSession.error ?? null
      }
      canAdvance={phaseAdvance.canAdvance}
      onStartSession={handleStart}
      onAdvancePhase={handleAdvance}
      onDeleteSession={handleDelete}
    />
  );
}

export const clubsVotingRoute = createRoute({
  getParentRoute: () => clubsRoute,
  path: "/$id/voting",
  component: () => (
    <ClubGuard requireClubId>
      <VotingCockpit />
    </ClubGuard>
  ),
});
